<template>
  <div class="wizard-progress">
    <v-container fluid class="py-4 px-4">
      <v-row justify="center">
        <v-col cols="12" lg="11">
          <div v-if="!isUploadStep" class="horizontal-stepper mb-4">
            <div 
              v-for="(step, index) in steps" 
              :key="step"
              class="stepper-item"
              :class="getStepClasses(step, index)"
            >
              <div class="step-indicator">
                <v-icon v-if="isStepCompleted(index)" small color="white">mdi-check</v-icon>
                <span v-else class="step-number">{{ index + 1 }}</span>
              </div>
              <div class="step-label">{{ getStepLabel(step) }}</div>
              <div v-if="index < steps.length - 1" class="step-connector"></div>
            </div>
          </div>

          <div class="step-header-content">
            <div class="step-info">
              <div class="step-title">
                {{ currentStepConfig.title }}
              </div>
              <div class="step-subtitle" v-if="currentStepConfig.subtitle">
                {{ currentStepConfig.subtitle }}
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { STEP_CONFIG, WIZARD_STEPS } from '../composables/useWizardNavigation';

export default {
  name: 'WizardProgress',
  props: {
    currentStep: {
      type: String,
      required: true
    },
    steps: {
      type: Array,
      required: true
    },
    issueCounts: {
      type: Object,
      default: () => ({})
    },
    resolvedSteps: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    currentStepIndex() {
      return this.steps.indexOf(this.currentStep);
    },
    totalSteps() {
      return this.steps.length;
    },
    isUploadStep() {
      return this.currentStep === WIZARD_STEPS.UPLOAD;
    },
    currentStepConfig() {
      return STEP_CONFIG[this.currentStep] || {
        title: 'Loading...',
        subtitle: ''
      };
    }
  },
  methods: {
    getIssueCount(step) {
      return this.issueCounts[step] || 0;
    },
    getStepLabel(step) {
      const labels = {
        upload: 'Upload',
        summary: 'Summary',
        'missing-fields': 'Missing Fields',
        duplicates: 'Duplicates',
        whitespace: 'Whitespace',
        harmonisation: 'Standardise',
        review: 'Review'
      };
      return labels[step] || step;
    },
    isStepCompleted(index) {
      return index < this.currentStepIndex;
    },
    isStepResolved(step) {
      return this.resolvedSteps[step] === true;
    },
    getStepClasses(step, index) {
      const hasIssues = this.getIssueCount(step) > 0 && !this.isStepResolved(step);
      const isHarmonisation = step === 'harmonisation';
      
      return {
        'stepper-item--completed': index < this.currentStepIndex,
        'stepper-item--active': index === this.currentStepIndex,
        'stepper-item--pending': index > this.currentStepIndex,
        'stepper-item--resolved': this.isStepResolved(step),
        'stepper-item--has-issues': hasIssues && !isHarmonisation,
        'stepper-item--has-warnings': hasIssues && isHarmonisation
      };
    }
  }
};
</script>

<style scoped>
.wizard-progress {
  border-bottom: 1px solid #e0e0e0;
  background: #ffffff;
}

.horizontal-stepper {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.stepper-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1;
  min-width: 0;
}

.step-indicator {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 6px;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
}

.stepper-item--completed .step-indicator {
  background-color: var(--v-success-base, #4caf50);
  color: white;
}

.stepper-item--active .step-indicator {
  background-color: var(--v-primary-base, #1976d2);
  color: white;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.4);
}

.stepper-item--pending .step-indicator {
  background-color: #e0e0e0;
  color: #757575;
}

.stepper-item--has-issues .step-indicator {
  background-color: #ef5350;
  color: white;
}

.stepper-item--has-issues.stepper-item--active .step-indicator {
  background-color: var(--v-primary-base, #1976d2);
}

.stepper-item--has-warnings .step-indicator {
  background-color: #ff9800;
  color: white;
}

.stepper-item--has-warnings.stepper-item--active .step-indicator {
  background-color: var(--v-primary-base, #1976d2);
}

.step-label {
  font-size: 12px;
  font-weight: 500;
  color: #757575;
  text-align: center;
  white-space: nowrap;
}

.stepper-item--active .step-label {
  color: var(--v-primary-base, #1976d2);
  font-weight: 600;
}

.stepper-item--completed .step-label {
  color: var(--v-success-base, #4caf50);
}

.step-connector {
  position: absolute;
  top: 16px;
  left: calc(50% + 20px);
  right: calc(-50% + 20px);
  height: 2px;
  background-color: #e0e0e0;
}

.stepper-item--completed .step-connector {
  background-color: var(--v-success-base, #4caf50);
}

.issue-count {
  font-size: 12px;
  font-weight: 600;
}

.step-number {
  font-size: 13px;
}

.step-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
}

.step-info {
  flex: 1;
}

.step-title {
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.3;
  margin-bottom: 2px;
}

.step-subtitle {
  font-size: 14px;
  color: #616161;
}
</style>
